import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaSearch, FaLink, FaUsers, FaCogs, FaCoins, FaStar, FaInfoCircle, FaCube } from 'react-icons/fa';
import AdsComponent from './AdsComponent';
import './PoolsList.css'; 

const PoolsList = () => {
  const [allPools, setAllPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);

  const relevanceFactor = 1000;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`/api/pool-all`);
        const poolsData = response.data;

        const sortedData = poolsData.sort((a, b) => {
          const relevanceA = a.total_coins + a.total_workers + a.total_blocks_24h;
          const relevanceB = b.total_coins + b.total_workers + b.total_blocks_24h;
          return relevanceB - relevanceA;
        });

        setAllPools(sortedData);
        setLoading(false);
      } catch (err) {
        setError('Error fetching pool data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleImageError = (e) => {
    e.target.src = '/img/pools/default.webp';
  };

  const getStars = (relevance) => {
    let stars = 1; // Mínimo de 1 estrella

    if (relevance > 40000 * relevanceFactor && relevance <= 100000 * relevanceFactor) {
      stars = 2;
    } else if (relevance > 100000 * relevanceFactor && relevance <= 200000 * relevanceFactor) {
      stars = 3;
    } else if (relevance > 200000 * relevanceFactor && relevance <= 400000 * relevanceFactor) {
      stars = 4;
    } else if (relevance > 400000 * relevanceFactor) {
      stars = 5;
    }

    let starColor = 'gray'; // defecto
    if (stars === 5) {
      starColor = '#32CD32'; // Verde para 5 estrellas
    } else if (stars >= 3) {
      starColor = '#FFD700'; // Amarillo para 3-4 estrellas
    } else {
      starColor = '#C0C0C0'; // Gris para 1-2 estrellas
    }

    return Array.from({ length: stars }, (_, index) => (
      <FaStar key={index} className="PoolsList-star" style={{ color: starColor }} />
    ));
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const filteredPools = allPools.filter(pool =>
    pool.pool_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pool.url_pool.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="PoolsList-loading">Loading...</div>;
  }

  if (error) {
    return <div className="PoolsList-error">{error}</div>;
  }

  return (
    <div>
      <h2>Mining Pools</h2>
      <div className="PoolsList-info-card">
        <h2><FaUsers /> What is a Mining Pool?</h2>
        <p>
          <FaCogs /> A mining pool is a group of cryptocurrency miners who combine their computational resources over a network. 
          The purpose of a mining pool is to increase the chances of finding a block and earning rewards. 
          <FaCoins /> The rewards are then shared among the participants in proportion to the amount of computational power they contributed.
        </p>

        <button onClick={toggleModal} className="PoolsList-info-button">
          <FaInfoCircle /> See Relevance Details
        </button>
      </div>

      <div className="sidebar-ad">
        <AdsComponent requiredSize="728x90" />
      </div>
      <div className="PoolsList-search-container">
        <FaSearch className="PoolsList-search-icon" />
        <input 
          type="text"
          placeholder="Search by pool name or URL..."
          className="PoolsList-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="PoolsList-card-grid">
        {filteredPools.map((pool, index) => {
          const relevance = pool.total_coins + pool.total_workers + pool.total_blocks_24h;
          return (
            <div key={index} className="PoolsList-card">
              <Link to={`/pool/${encodeURIComponent(pool.pool_name)}`}>
                <img 
                  src={`/img/pools/${pool.pool_name.toLowerCase()}.webp`} 
                  alt={pool.pool_name} 
                  className="PoolsList-pool-logo" 
                  onError={handleImageError} 
                />
              </Link>
              <Link to={`/pool/${encodeURIComponent(pool.pool_name)}`} className="PoolsList-pool-name">
                <h3>{pool.pool_name}</h3>
              </Link>
              <Link to={`/pool/${encodeURIComponent(pool.pool_name)}`} className="PoolsList-link">
                <FaLink /> {pool.url_pool}
              </Link>
              <a href={pool.url_api} target="_blank" rel="noopener noreferrer" className="PoolsList-api-link">
                API URL
              </a>
              <div className="PoolsList-stats">
                <p><FaCoins /> Total Coins: {pool.total_coins}</p>
                <p><FaUsers /> Total Workers: {pool.total_workers}</p>
                <p><FaUsers /> Solo Workers: {pool.total_workers_solo}</p>
                <p><FaCube /> Total Blocks (24h): {pool.total_blocks_24h}</p>
                <p>Relevance: {getStars(relevance)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="PoolsList-modal-overlay">
          <div className="PoolsList-modal">
            <h3>Relevance Details</h3>
            <p>
              The relevance of each pool is calculated based on the sum of the total coins, total workers, and total blocks mined in the last 24 hours.
              The more coins, workers, and blocks a pool has, the higher its relevance score. Here is the breakdown of the stars:
            </p>
            <ul>
              <li>⭐ 1-2 stars: Relevance between 0 and {40000 * relevanceFactor}</li>
              <li>⭐ 3-4 stars: Relevance between {40001 * relevanceFactor} and {200000 * relevanceFactor}</li>
              <li>⭐ 5 stars: Relevance greater than {400000 * relevanceFactor}</li>
            </ul>
            <button onClick={toggleModal} className="PoolsList-modal-close">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoolsList;
